"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Send } from "lucide-react";

export default function LoyaltyCampaignScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("SMS campaigns · Add-on", "Campañas SMS · Add-on")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Tuesday is slow? ", "¿Martes lento? ")}
            <span className="text-redy-charcoal/30">{t("Text your top 100.", "Mándale SMS a tus top 100.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Pick a segment (\"top spenders\", \"haven't visited in 30 days\", \"birthday this week\"), write the SMS, send — only to customers who opted in. No marketplace takes a cut of the orders it brings back.",
              "Elige un segmento (\"top spenders\", \"sin visitar en 30 días\", \"cumpleaños esta semana\"), escribe el SMS, envía — solo a clientes que aceptaron recibirlos. Ningún marketplace se lleva una parte de las órdenes que te trae de vuelta."
            )}
          </p>
        </motion.div>

        {/* Campaign composer mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6 max-w-[1000px] mx-auto"
        >
          {/* Composer */}
          <div className="md:col-span-7">
            <div className="bg-[#FAFAF8] border border-black/5 rounded-[24px] p-6">
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-4">
                {t("New campaign", "Nueva campaña")}
              </div>

              {/* Segment picker */}
              <div className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/65 mb-2">
                  {t("Audience segment", "Segmento")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: t("Top spenders", "Top spenders"), count: "100", selected: true },
                    { name: t("Last 30 days", "Últimos 30 días"), count: "412" },
                    { name: t("Birthday this week", "Cumple esta semana"), count: "18" },
                    { name: t("Haven't visited in 60 days", "Sin visitar 60d"), count: "234" },
                  ].map((seg, i) => (
                    <motion.div
                      key={seg.name}
                      initial={{ opacity: 0, y: 6 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.35, delay: 0.2 + 0.08 * i }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold ${
                        seg.selected ? "text-white" : "bg-white border border-black/5 text-redy-secondary"
                      }`}
                      style={
                        seg.selected
                          ? {
                              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                            }
                          : undefined
                      }
                    >
                      <span>{seg.name}</span>
                      <span className={seg.selected ? "text-white/75" : "text-redy-secondary/50"}>
                        · {seg.count}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Message textarea */}
              <div className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/65 mb-2">
                  {t("Message", "Mensaje")}
                </div>
                <div className="bg-white border border-black/5 rounded-[14px] p-4 min-h-[110px]">
                  <div className="text-[14px] text-redy-charcoal leading-relaxed">
                    {t(
                      "Hey {first_name} — Birria Plate at Pepitos this Tuesday only: $8 (normally $12). Show this text. — Maria",
                      "Hola {first_name} — Birria Plate en Pepitos este martes solo: $8 (normalmente $12). Muestra este SMS. — Maria"
                    )}
                  </div>
                </div>
                <div className="flex justify-between mt-2 text-[10px] font-bold text-redy-secondary/55">
                  <span>{t("Personalized with {first_name}", "Personalizado con {first_name}")}</span>
                  <span>113 chars · 1 SMS</span>
                </div>
              </div>

              {/* Send button */}
              <div
                className="rounded-[14px] p-4 flex items-center justify-between text-white"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                }}
              >
                <div className="flex items-center gap-2.5">
                  <Send className="w-4 h-4" />
                  <div>
                    <div className="text-[12px] font-black">{t("Send to 100 customers", "Enviar a 100 clientes")}</div>
                    <div className="text-[10px] font-bold text-white/85">
                      {t("100 opted-in customers", "100 clientes con opt-in")}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/20">
                  {t("Send", "Enviar")}
                </div>
              </div>
            </div>
          </div>

          {/* Preview phone */}
          <div className="md:col-span-5 flex items-start justify-center">
            <PhonePreview />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PhonePreview() {
  const { t } = useLang();
  return (
    <div className="w-[200px] h-[300px] bg-[#0a0a0a] rounded-[28px] p-[5px] shadow-[0_30px_70px_rgba(0,0,0,0.15)]">
      <div className="w-full h-full rounded-[22px] overflow-hidden bg-[#1A1A1A] relative flex flex-col">
        {/* Notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-14 h-3 bg-[#0a0a0a] rounded-full z-10" />
        {/* Status */}
        <div className="h-7 flex items-center justify-end px-3 text-[7px] font-bold text-white">5G</div>
        {/* Messages header */}
        <div className="px-3 py-1.5 border-b border-white/10 text-center">
          <div className="text-[9px] font-bold text-white">Pepitos</div>
          <div className="text-[7px] text-white/45">text · iMessage</div>
        </div>
        {/* Message bubble */}
        <div className="px-3 py-3 flex-1 flex items-end">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-[#26282C] rounded-[12px] rounded-bl-[3px] px-3 py-2 max-w-[85%] text-white"
          >
            <div className="text-[8px] leading-snug">
              {t(
                "Hey Alex — Birria Plate at Pepitos this Tuesday only: $8 (normally $12). Show this text. — Maria",
                "Hola Alex — Birria Plate en Pepitos este martes solo: $8 (normalmente $12). Muestra este SMS. — Maria"
              )}
            </div>
          </motion.div>
        </div>
        {/* Time */}
        <div className="text-center text-[7px] text-white/40 pb-2">
          11:42 AM · {t("Delivered", "Entregado")}
        </div>
      </div>
    </div>
  );
}
